/**
 * useRPCProvider Hook
 * Convenient hook for accessing RPC provider state and management
 */

import { useRPCProviderContext } from '@/contexts/RPCProviderContext';
import { ProviderId, ProviderStatus } from '@/services/rpc/types';

export interface UseRPCProviderReturn {
  /** Current active provider ID */
  activeProvider: ProviderId | null;
  /** HTTP RPC endpoint */
  httpEndpoint: string;
  /** WebSocket endpoint (if available) */
  wsEndpoint: string | undefined;
  /** User preference: 'auto' or specific provider */
  userPreference: 'auto' | ProviderId;
  /** Set user preference for provider selection */
  setUserPreference: (preference: 'auto' | ProviderId) => void;
  /** Manually switch to a specific provider */
  switchProvider: (providerId: ProviderId) => boolean;
  /** Status of all providers */
  providers: Record<ProviderId, ProviderStatus> | null;
  /** Whether a failover is currently in progress */
  isFailingOver: boolean;
  /** Whether the provider system is initialized */
  isInitialized: boolean;
  /** Get status of a specific provider */
  getProviderStatus: (providerId: ProviderId) => ProviderStatus | null;
  /** Whether the active provider supports webhooks */
  supportsWebhooks: boolean;
}

export function useRPCProvider(): UseRPCProviderReturn {
  const {
    state,
    httpEndpoint,
    wsEndpoint,
    setUserPreference,
    switchProvider,
    isInitialized,
  } = useRPCProviderContext();

  const getProviderStatus = (providerId: ProviderId): ProviderStatus | null => {
    return state?.providers[providerId] || null;
  };

  const activeProviderStatus = state?.activeProvider 
    ? state.providers[state.activeProvider] 
    : null;

  return {
    activeProvider: state?.activeProvider || null,
    httpEndpoint,
    wsEndpoint,
    userPreference: state?.userPreference || 'auto',
    setUserPreference,
    switchProvider,
    providers: state?.providers || null,
    isFailingOver: state?.isFailingOver || false,
    isInitialized,
    getProviderStatus,
    supportsWebhooks: activeProviderStatus?.supportsWebhooks || false,
  };
}
